import React, { useState, useRef } from "react";

import ReactCrop, { centerCrop, makeAspectCrop, Crop, PixelCrop, convertToPixelCrop } from "react-image-crop";
import { canvasPreview } from "./canvasPreview";
import { useDebounceEffect } from "./useDebounceEffect";

import "react-image-crop/dist/ReactCrop.css";

// This is to demonstate how to make and center a % aspect crop
// which is a bit trickier so we use some helper functions.
function centerAspectCrop(mediaWidth, mediaHeight, aspect) {
  return centerCrop(
    makeAspectCrop(
      {
        unit: "%",
        width: 90,
      },
      aspect,
      mediaWidth,
      mediaHeight
    ),
    mediaWidth,
    mediaHeight
  );
}

export default function ImageCropper({ showLoader, hideLoader, setHideEdit, fileUploadRef, updateAvatar }) {
  const [imgSrc, setImgSrc] = useState("");
  const previewCanvasRef = useRef(null);
  const imgRef = useRef(null);
  const blobUrlRef = useRef("");
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState();
  const [scale, setScale] = useState(1);
  const [rotate, setRotate] = useState(0);
  const [aspect, setAspect] = useState(1);

  function onSelectFile(e) {
    if (e.target.files && e.target.files.length > 0) {
      if (showLoader) showLoader();
      setCrop(undefined); // Makes crop preview update between images.
      const reader = new FileReader();
      reader.addEventListener("load", () => {
        setHideEdit(true);
        hideLoader();
        setImgSrc(reader.result?.toString() || "");
      });
      reader.readAsDataURL(e.target.files[0]);
    }
  }

  function onImageLoad(e) {
    if (aspect) {
      const { width, height } = e.currentTarget;
      setCrop(centerAspectCrop(width, height, aspect));
    }
  }

  async function onCrop() {
    const image = imgRef.current;
    const previewCanvas = previewCanvasRef.current;
    if (!image || !previewCanvas || !completedCrop) {
      throw new Error("Crop canvas does not exist");
    }

    // This will size relative to the uploaded image
    // size. If you want to size according to what they
    // are looking at on screen, remove scaleX + scaleY
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    const offscreen = new OffscreenCanvas(completedCrop.width * scaleX, completedCrop.height * scaleY);
    const ctx = offscreen.getContext("2d");
    if (!ctx) {
      throw new Error("No 2d context");
    }

    ctx.drawImage(previewCanvas, 0, 0, previewCanvas.width, previewCanvas.height, 0, 0, offscreen.width, offscreen.height);
    // You might want { type: "image/jpeg", quality: <0 to 1> } to
    // reduce image size
    const blob = await offscreen.convertToBlob({
      type: "image/png",
    });
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
    }
    //reset image ref
    setImgSrc(null);
    setCompletedCrop(true);
    setHideEdit(false);
    blobUrlRef.current = URL.createObjectURL(blob);

    updateAvatar(blobUrlRef.current);
  }

  useDebounceEffect(
    async () => {
      if (completedCrop?.width && completedCrop?.height && imgRef.current && previewCanvasRef.current) {
        // We use canvasPreview as it's much faster than imgPreview.
        canvasPreview(imgRef.current, previewCanvasRef.current, completedCrop, scale, rotate);
      }
    },
    100,
    [completedCrop, scale, rotate]
  );

  return (
    <div className="image-cropper">
      <div className="Crop-Controls">
        <input className="hidden-file-upload" type="file" accept="image/*" onChange={onSelectFile} ref={fileUploadRef} />
      </div>

      {/* ShowImage Over Black BG pos absolute */}
      {!!imgSrc && fileUploadRef.current.files.length > 0 && (
        <>
          <div className="crop-image-bg">
            <ReactCrop
              crop={crop}
              onChange={(_, percentCrop) => setCrop(percentCrop)}
              onComplete={(c) => setCompletedCrop(c)}
              aspect={aspect}
              keepSelection={true}
              // minWidth={400}
              minHeight={100}
              circularCrop
            >
              <img ref={imgRef} alt="Crop me" src={imgSrc}  style={{ transform: `rotate(${rotate}deg)` }} onLoad={onImageLoad} />
            </ReactCrop>
          </div>

          {/* ROT */}
          <input className="rotate-slider" name="rotate-image" defaultValue={0} onChange={(e) => setRotate(e.target.value)} min={0} max={360} type="range" />
          <label htmlFor="">{rotate}deg</label>

          {/* CROP BUTTON */}
          <button className="standard-button crop-button" onClick={onCrop}>
            Crop Image
          </button>
        </>
      )}

      {!!completedCrop && (
        <>
          <div className="hidden-canvas">
            <canvas
              ref={previewCanvasRef}
              style={{
                border: "1px solid black",
                objectFit: "contain",
                width: completedCrop.width,
                height: completedCrop.height,
              }}
            />
          </div>
        </>
      )}
    </div>
  );
}
